import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dialog", "frame", "status", "spinner", "message", "recovery"]
  static values = { conversationsUrl: String }

  open(event) {
    const link = event.target.closest("a")
    if (!link || this.element.contains(link) || link.target === "_blank") return

    const url = new URL(event.detail.url)
    const conversationsPath = new URL(this.conversationsUrlValue, window.location.origin).pathname
    if (url.origin !== window.location.origin ||
        (url.pathname !== conversationsPath && !url.pathname.startsWith(`${conversationsPath}/`))) return

    event.preventDefault()
    event.detail.originalEvent.preventDefault()
    window.clearTimeout(this.closeTimer)
    this.closeTimer = null
    // Capture the page at opening time, including any current filters.
    this.returnTo = window.location.pathname + window.location.search
    url.searchParams.set("return_to", this.returnTo)
    url.searchParams.delete("animate")
    this.returnFocus = link
    document.body.classList.add("assistant-panel-open")
    this.dialogTarget.showModal()

    const workspace = this.frameTarget.querySelector(".assistant-workspace-modal")
    workspace?.classList.remove("is-closing")
    if (this.loadedUrl && (url.href === this.requestedUrl || url.pathname === conversationsPath)) {
      this.focusInput()
      return
    }

    this.requestedUrl = url.href
    this.load()
  }

  load() {
    this.loadedUrl = null
    this.statusTarget.classList.remove("d-none")
    this.spinnerTarget.classList.remove("d-none")
    this.recoveryTarget.classList.add("d-none")
    this.messageTarget.textContent = "Loading assistant…"
    const sameSource = this.frameTarget.src === this.requestedUrl
    this.frameTarget.src = this.requestedUrl
    if (sameSource) this.frameTarget.reload()
  }

  loaded() {
    this.loadedUrl = this.frameTarget.src
    this.statusTarget.classList.add("d-none")
    this.focusInput()
  }

  failed(event) {
    event.preventDefault()
    this.loadedUrl = null
    this.spinnerTarget.classList.add("d-none")
    this.statusTarget.classList.remove("d-none")
    this.recoveryTarget.classList.remove("d-none")
    this.messageTarget.textContent = "The assistant could not be loaded. Please try again."
  }

  retry() {
    this.load()
  }

  cancel(event) {
    if (event.target !== this.dialogTarget) return

    event.preventDefault()
    this.close()
  }

  setReturnTo(event) {
    // Broadcast forms do not have the originating page's request context.
    const form = event.target
    if (!(form instanceof HTMLFormElement) || !this.returnTo) return

    let input = form.querySelector('input[name="return_to"]')
    if (!input) {
      input = document.createElement("input")
      input.type = "hidden"
      input.name = "return_to"
      form.append(input)
    }
    input.value = this.returnTo
  }

  close(event) {
    event?.preventDefault()
    if (!this.dialogTarget.open || this.closeTimer) return

    const workspace = this.frameTarget.querySelector(".assistant-workspace-modal")
    if (workspace) this.application.getControllerForElementAndIdentifier(workspace, "assistant-workspace")?.rememberState()
    workspace?.classList.remove("assistant-workspace-modal--entering")
    workspace?.classList.add("is-closing")
    const duration = window.matchMedia("(prefers-reduced-motion: reduce)").matches || !workspace ? 0 : 220
    this.closeTimer = window.setTimeout(() => {
      this.reset()
      this.returnFocus?.focus({ preventScroll: true })
    }, duration)
  }

  reset() {
    window.clearTimeout(this.closeTimer)
    this.closeTimer = null
    this.dialogTarget.close()
    document.body.classList.remove("assistant-panel-open")
  }

  focusInput() {
    if (!this.dialogTarget.open) return

    const workspace = this.frameTarget.querySelector(".assistant-workspace-modal")
    const controller = workspace && this.application.getControllerForElementAndIdentifier(workspace, "assistant-workspace")
    if (controller?.followLatest) controller.scrollToLatest(false)

    const input = this.frameTarget.querySelector(".assistant-composer__input:not(:disabled)")
    input?.focus({ preventScroll: true })
    input?.dispatchEvent(new Event("input", { bubbles: true }))
  }

  disconnect() {
    this.reset()
  }
}
